import type { ProductType } from "./products"
import type { ServiceId } from "./services"
import { catalogPriceId, imageBaseURL } from "@/lib/utils"

export type TreatmentId =
	| "acidez"
	| "acne"
	| "ansiedad"
	| "artritis"
	| "amenorrea"
	| "asma"
	| "bronquitis"
	| "cancer"
	| "cataratas"
	| "conjuntivitis"
	| "dermatitis"
	| "diabetes"
	| "diabetes-tipo-2"
	| "envejecimiento-prematuro"
	| "estrenimiento"
	| "heridas"
	| "menopausia"
	| "parkinson"
	| "prostatitis"
	| "sobrepeso"
	| "vph"

export type TreatmentWeeks = "two" | "four"

export type TreatmentPackage = "basico" | "completo"

/**
 * ID de catálogo por paquete (Básico/Completo), usado para que las promociones
 * (`/admin/promociones`) apliquen a un paquete específico en vez del tratamiento
 * completo. Debe coincidir con los `catalogId` de `CATALOG_ITEMS` en
 * `packages/shared/src/catalog-prices.ts`.
 */
export function treatmentCatalogId(id: TreatmentId, weeks: TreatmentWeeks): string {
	return `${id}-${weeks === "two" ? "basico" : "completo"}`
}

export interface TreatmentProductRef {
	id: ProductType | ServiceId
	amount: number
}

export interface TreatmentDurationOption {
	priceId: string
	image: string
	products: TreatmentProductRef[]
}

export type TreatmentDuration = Record<TreatmentWeeks, TreatmentDurationOption>

export interface TreatmentProps {
	id: TreatmentId
	name: string
	as: "el" | "la" | "los" | "las"
	duration: TreatmentDuration
}

const img = (file: string): string => imageBaseURL(`treatments/${file}`)

const TREATMENTS: Record<TreatmentId, TreatmentProps> = {
	acidez: {
		id: "acidez",
		name: "Acidez",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("acidez", "two")),
				image: img("acidez-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("acidez", "four")),
				image: img("acidez-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 12 }
				]
			}
		}
	},
	acne: {
		id: "acne",
		name: "Acné",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("acne", "two")),
				image: img("acne-basico.jpg"),
				products: [
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("acne", "four")),
				image: img("acne-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			}
		}
	},
	ansiedad: {
		id: "ansiedad",
		name: "Ansiedad",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("ansiedad", "two")),
				image: img("ansiedad-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "dermogel", amount: 1 },
					{ id: "taftefet", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("ansiedad", "four")),
				image: img("ansiedad-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 }
				]
			}
		}
	},
	artritis: {
		id: "artritis",
		name: "Artritis",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("artritis", "two")),
				image: img("artritis-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("artritis", "four")),
				image: img("artritis-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 9 },
					{ id: "dermogel", amount: 1 }
				]
			}
		}
	},
	amenorrea: {
		id: "amenorrea",
		name: "Amenorrea",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("amenorrea", "two")),
				image: img("amenorrea-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 4 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("amenorrea", "four")),
				image: img("amenorrea-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 10 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			}
		}
	},
	asma: {
		id: "asma",
		name: "Asma",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("asma", "two")),
				image: img("asma-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "taftefet", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("asma", "four")),
				image: img("asma-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 },
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 }
				]
			}
		}
	},
	bronquitis: {
		id: "bronquitis",
		name: "Bronquitis",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("bronquitis", "two")),
				image: img("bronquitis-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 4 },
					{ id: "taftefet", amount: 1 },
					{ id: "te", amount: 4 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("bronquitis", "four")),
				image: img("bronquitis-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 8 },
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "te", amount: 4 }
				]
			}
		}
	},
	cancer: {
		id: "cancer",
		name: "Cáncer",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("cancer", "two")),
				image: img("cancer-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 4 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 4 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("cancer", "four")),
				image: img("cancer-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 8 },
					{ id: "dermogel", amount: 1 },
					{ id: "taftefet", amount: 1 },
					{ id: "te", amount: 10 }
				]
			}
		}
	},
	cataratas: {
		id: "cataratas",
		name: "Cataratas",
		as: "las",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("cataratas", "two")),
				image: img("cataratas-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "taftefet", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("cataratas", "four")),
				image: img("cataratas-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "taftefet", amount: 1 },
					{ id: "agua-vita", amount: 6 }
				]
			}
		}
	},
	conjuntivitis: {
		id: "conjuntivitis",
		name: "Conjuntivitis",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("conjuntivitis", "two")),
				image: img("conjuntivitis-basico.jpg"),
				products: [
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("conjuntivitis", "four")),
				image: img("conjuntivitis-completo.jpg"),
				products: [
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 },
					{ id: "dermogel", amount: 1 }
				]
			}
		}
	},
	dermatitis: {
		id: "dermatitis",
		name: "Dermatitis",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("dermatitis", "two")),
				image: img("dermatitis-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("dermatitis", "four")),
				image: img("dermatitis-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			}
		}
	},
	diabetes: {
		id: "diabetes",
		name: "Diabetes Tipo 1",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("diabetes", "two")),
				image: img("diabetes-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 5 },
					{ id: "taftefet", amount: 1 },
					{ id: "te", amount: 5 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("diabetes", "four")),
				image: img("diabetes-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "taftefet", amount: 1 },
					{ id: "te", amount: 10 }
				]
			}
		}
	},
	"diabetes-tipo-2": {
		id: "diabetes-tipo-2",
		name: "Diabetes Tipo 2",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("diabetes-tipo-2", "two")),
				image: img("diabetes2-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 3 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("diabetes-tipo-2", "four")),
				image: img("diabetes2-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "taftefet", amount: 1 },
					{ id: "te", amount: 6 }
				]
			}
		}
	},
	"envejecimiento-prematuro": {
		id: "envejecimiento-prematuro",
		name: "Envejecimiento Prematuro",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("envejecimiento-prematuro", "two")),
				image: img("envejecimiento-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "dermogel", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("envejecimiento-prematuro", "four")),
				image: img("envejecimiento-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "dermogel", amount: 1 },
					{ id: "jabon-neutro", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "agua-vita", amount: 5 }
				]
			}
		}
	},
	estrenimiento: {
		id: "estrenimiento",
		name: "Estreñimiento",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("estrenimiento", "two")),
				image: img("estrenimiento-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 },
					{ id: "te", amount: 6 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("estrenimiento", "four")),
				image: img("estrenimiento-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 8 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 10 }
				]
			}
		}
	},
	heridas: {
		id: "heridas",
		name: "Heridas",
		as: "las",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("heridas", "two")),
				image: img("heridas-basico.jpg"),
				products: [
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("heridas", "four")),
				image: img("heridas-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			}
		}
	},
	menopausia: {
		id: "menopausia",
		name: "Menopausia",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("menopausia", "two")),
				image: img("menopausia-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 3 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("menopausia", "four")),
				image: img("menopausia-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "dieta", amount: 1 },
					{ id: "te", amount: 6 }
				]
			}
		}
	},
	parkinson: {
		id: "parkinson",
		name: "Parkinson",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("parkinson", "two")),
				image: img("parkinson-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 4 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 4 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("parkinson", "four")),
				image: img("parkinson-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 8 },
					{ id: "dermogel", amount: 1 },
					{ id: "taftefet", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "te", amount: 10 }
				]
			}
		}
	},
	prostatitis: {
		id: "prostatitis",
		name: "Prostatitis",
		as: "la",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("prostatitis", "two")),
				image: img("prostatitis-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 3 },
					{ id: "te", amount: 3 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("prostatitis", "four")),
				image: img("prostatitis-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 6 },
					{ id: "dermogel", amount: 1 },
					{ id: "te", amount: 10 }
				]
			}
		}
	},
	sobrepeso: {
		id: "sobrepeso",
		name: "Sobrepeso",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("sobrepeso", "two")),
				image: img("sobrepeso-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "agua-vita", amount: 4 },
					{ id: "te", amount: 4 },
					{ id: "dieta", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("sobrepeso", "four")),
				image: img("sobrepeso-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 8 },
					{ id: "te", amount: 10 },
					{ id: "dieta", amount: 1 }
				]
			}
		}
	},
	vph: {
		id: "vph",
		name: "Virus del Papiloma Humano",
		as: "el",
		duration: {
			two: {
				priceId: catalogPriceId(treatmentCatalogId("vph", "two")),
				image: img("vph-basico.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 1 },
					{ id: "dermogel", amount: 1 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			},
			four: {
				priceId: catalogPriceId(treatmentCatalogId("vph", "four")),
				image: img("vph-completo.jpg"),
				products: [
					{ id: "shalosh-bayom", amount: 2 },
					{ id: "agua-vita", amount: 3 },
					{ id: "dermogel", amount: 2 },
					{ id: "lazihum", amount: 1 },
					{ id: "jabon-neutro", amount: 1 }
				]
			}
		}
	}
}

export const treatmentSlugs: TreatmentId[] = Object.keys(TREATMENTS) as TreatmentId[]

export const getTreatmentById = (id: TreatmentId): TreatmentProps => TREATMENTS[id]

export const isTreatment = (id: string): id is TreatmentId => id in TREATMENTS

/**
 * Índice inverso: tratamientos (padecimientos) que incluyen un producto dado en
 * cualquiera de sus duraciones. Cada tratamiento aparece una sola vez. Base para
 * la sección "Padecimientos tratados" de la ficha de producto — enlaza a
 * `/tratamientos/:slug`. Los productos base (p. ej. `shalosh-bayom`, `agua-vita`)
 * se usan en casi todos los tratamientos, así que la lista puede ser larga.
 */
export const getTreatmentsForProduct = (productId: ProductType): TreatmentProps[] =>
	treatmentSlugs
		.map(getTreatmentById)
		.filter(treatment => Object.values(treatment.duration).some(option => option.products.some(ref => ref.id === productId)))

export default TREATMENTS
